export const StepType = {
  PAGE_DETECTION: "PAGE_DETECTION",
  OCR: "OCR",
  DATA_EXTRACTION: "DATA_EXTRACTION",
} as const;

export type StepType = (typeof StepType)[keyof typeof StepType];

export const FlowType = {
  FILE_ONLY: "FILE_ONLY",
  BASIC: "BASIC",
  PAGE_DETECTION: "PAGE_DETECTION",
  NO_OCR_PAGE_DETECTION: "NO_OCR_PAGE_DETECTION",
  PARALLEL: "PARALLEL",
} as const;

export type FlowType = (typeof FlowType)[keyof typeof FlowType];

export const ProviderType = {
  OCR: "OCR",
  LLM: "LLM",
};

export type ProviderType = (typeof ProviderType)[keyof typeof ProviderType];

// ============================================================================
// Document Type Models
// ============================================================================

export interface DocumentTypeInfoModel {
  doctype_code: string;
  doctype_name: string;
  logo_url: string | null;
  description: string | null;
  category_id: string | null;
  category_name: string | null;
  category_description: string | null;
  status: string;
}

export interface DocumentTypeInfoViewModel {
  docTypeCode: string;
  docTypeName: string;
  logoUrl: string | null;
  description: string | null;
  categoryId: string | null;
  categoryName: string | null;
  categoryDescription: string | null;
  status: string;
}

/** Doctype → mapping-service routing. When `enabled`, completed items of this
 *  doctype are dispatched to the mapping service `(service, plugin_id)`. */
export interface MappingConfigModel {
  enabled: boolean;
  service: string | null;
  plugin_id: string | null;
}

export interface MappingConfigViewModel {
  enabled: boolean;
  service: string | null;
  pluginId: string | null;
}

export interface DocumentTypeDetailModel {
  id: string;
  doctype_code: string;
  doctype_name: string;
  logo_url: string | null;
  description: string | null;
  category_id: string | null;
  category_name: string | null;
  organization_id: string | null;
  status: string;
  mapping_config: MappingConfigModel | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface DocumentTypeDetailViewModel {
  id: string;
  docTypeCode: string;
  docTypeName: string;
  logoUrl: string | null;
  description: string | null;
  categoryId: string | null;
  categoryName: string | null;
  organizationId: string | null;
  status: string;
  mappingConfig: MappingConfigViewModel | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export interface CreateDocumentTypeRequest {
  docTypeCode: string;
  docTypeName: string;
  categoryId: string;
  description?: string;
  status?: string;
  logo?: File;
  mappingEnabled?: boolean;
  mappingService?: string | null;
  mappingPluginId?: string | null;
}

export interface UpdateDocumentTypeRequest {
  docTypeCode?: string;
  docTypeName?: string;
  categoryId?: string;
  description?: string;
  status?: string;
  logo?: File;
  mappingEnabled?: boolean;
  mappingService?: string | null;
  mappingPluginId?: string | null;
}

export interface DocumentTypeResponse {
  id: string;
  doctype_code: string;
  doctype_name: string;
  description: string | null;
  status: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface DocumentTypeViewModel {
  id: string;
  docTypeCode: string;
  docTypeName: string;
  description: string | null;
  status: string;
  createdAt: Date | null;
  updatedAt: Date | null;
}

// ============================================================================
// Flow Configuration Models (New API Structure)
// ============================================================================

export interface LLMConfigOptions {
  temperature?: number | null;
  top_p?: number | null;
  top_k?: number | null;
  max_output_tokens?: number | null;
  thinking_budget?: number | null;
  seed?: number | null;
}

export interface LLMConfigOptionsViewModel {
  temperature?: number | null;
  topP?: number | null;
  topK?: number | null;
  maxOutputTokens?: number | null;
  thinkingBudget?: number | null;
  seed?: number | null;
}

export interface LLMFlowStepConfigResponse {
  provider: string;
  system_prompt: string;
  user_prompt: string;
  model_name: string;
  config_options: LLMConfigOptions | null;
  response_schema: Record<string, unknown> | null;
  executions: AgentExecutionResponse[] | null;
}

export interface LLMFlowStepConfigViewModel {
  provider: string;
  systemPrompt: string;
  userPrompt: string;
  modelName: string;
  configOptions: LLMConfigOptionsViewModel | null;
  responseSchema: Record<string, unknown> | null;
  executions: AgentExecutionViewModel[] | null;
}

export interface OCRFlowStepConfigResponse {
  provider: string;
  model_name: string;
  ocr_options: Record<string, unknown> | null;
}

export interface OCRFlowStepConfigViewModel {
  provider: string;
  modelName: string;
  ocrOptions: Record<string, unknown> | null;
}

// ============================================================================
// Parallel Extraction Config Models
// ============================================================================

export interface AgentExecutionResponse {
  domain_key: string;
  user_prompt: string;
  response_schema: Record<string, unknown>;
  system_prompt: string | null;
  model_name: string | null;
  config_options: LLMConfigOptions | null;
}

export interface AgentExecutionViewModel {
  domainKey: string;
  userPrompt: string;
  responseSchema: Record<string, unknown>;
  systemPrompt: string | null;
  modelName: string | null;
  configOptions: LLMConfigOptionsViewModel | null;
}

export interface FlowStepResponse {
  step_order: number;
  step_type: string;
  llm_step_config?: LLMFlowStepConfigResponse | null;
  ocr_step_config?: OCRFlowStepConfigResponse | null;
}

export interface FlowStepViewModel {
  stepOrder: number;
  stepType: string;
  llmStepConfig?: LLMFlowStepConfigViewModel | null;
  ocrStepConfig?: OCRFlowStepConfigViewModel | null;
}

// Flow version summary (for listing)
export interface FlowVersionSummaryResponse {
  id: string;
  flow_base_id: string;
  version: number;
  is_active: boolean;
  flow_type: string;
  created_at: string | null;
  updated_at: string | null;
}

export interface FlowVersionSummaryViewModel {
  id: string;
  flowBaseId: string;
  version: number;
  isActive: boolean;
  flowType: string;
  createdAt: Date | null;
  updatedAt: Date | null;
}

// Full flow configuration (for detail view)
export interface FlowConfigResponse {
  id: string;
  flow_base_id: string;
  doctype_id: string;
  flow_type: string;
  flow_description: string | null;
  version: number;
  steps: FlowStepResponse[];
  is_active: boolean;
  updated_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface FlowConfigViewModel {
  id: string;
  flowBaseId: string;
  docTypeId: string;
  flowType: string;
  flowDescription: string | null;
  version: number;
  steps: FlowStepViewModel[];
  isActive: boolean;
  updatedBy: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

// ============================================================================
// Request Models
// ============================================================================

export interface LLMFlowStepConfigRequest {
  provider: string;
  system_prompt: string;
  user_prompt: string;
  model_name: string;
  config_options?: LLMConfigOptions | null;
  response_schema?: Record<string, unknown> | null;
  executions?: AgentExecutionRequest[] | null;
}

export interface OCRFlowStepConfigRequest {
  provider: string;
  model_name: string;
  ocr_options?: Record<string, unknown> | null;
}

export interface AgentExecutionRequest {
  domain_key: string;
  user_prompt: string;
  response_schema: Record<string, unknown>;
  system_prompt?: string | null;
  model_name?: string | null;
  config_options?: LLMConfigOptions | null;
}

export interface FlowStepRequest {
  step_order: number;
  step_type: string;
  llm_step_config?: LLMFlowStepConfigRequest;
  ocr_step_config?: OCRFlowStepConfigRequest;
}

export interface CreateFlowConfigRequest {
  flow_base_id?: string | null;
  flow_type: string;
  flow_description?: string | null;
  steps: FlowStepRequest[];
}

export interface ActivateFlowRequest {
  flow_base_id: string;
  version: number;
}

export interface UpdateFlowRequest {
  status: string;
  flow_description?: string | null;
}

// ============================================================================
// Common Response Models
// ============================================================================

export interface DeleteResponse {
  success: boolean;
  message: string;
  deleted_id: string;
}
